import { isPrivateURL } from '../url/isPrivateURL';
import { isCorsMode } from './credentials';
import { shouldSignRequest } from './hooks/beforeRequest/dpop';
import { isServer } from './utils';

/**
 * Whether these bytes can only be fetched by us, through `executeFetch`.
 *
 * The backend may demand a DPoP proof on our own urls, and only a request this layer makes carries
 * one — a browser navigation (`<a download>`, `window.open`, an `<img src>`) has no header and 401s.
 *
 * False cross-origin: `executeFetch` forces `credentials: 'include'`, and a download chain ends on
 * presigned storage answering `Access-Control-Allow-Origin: *`, which the browser rejects under
 * `include`. That is `matrix-spa` and `sdk-react`, NOT `apps/matrix`.
 */
export function requiresSignedFetch(url: string): boolean {
  // a delta-matrix embed leaves __unifyBaseUrl unset, so a page-relative url would resolve to the
  // CUSTOMER's origin and we would fetch their /api — where a 401 fires our logout on their page
  if (isServer() || isCorsMode() || '__unifyTenantDomain' in window) return false;

  try {
    // origin as the base: a `blob:` or `data:` url inherits it and would otherwise look signable
    const parsed = new URL(url, window.location.origin);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
    // a capability route needs no proof — its token IS the credential. Pathname only, or a
    // capability url in a query string would exempt the mint route carrying it.
    if (isPrivateURL(parsed.pathname)) return false;
    return shouldSignRequest(parsed);
  } catch {
    return false;
  }
}
